import DictionaryAccordion from "./DictionaryAccordion";
import { useCustomerTags } from "./useCustomerTags";
import { IconButton, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField } from "@mui/material";
import type { FC } from "react";
import { useState } from "react";
import { Delete as DeleteIcon, Edit as EditIcon } from "@mui/icons-material";
import type { CustomerTag, CustomerTagCategory } from "./customerTagsApi";
import AsyncLoadingButton from "../../components/asyncLoadingButton/AsyncLoadingButton";
import { Select } from "../../components/select/Select";
import EditCustomerTagDialog from "./EditCustomerTagDialog";

const categories: CustomerTagCategory[] = [
  "Obszar",
  "Oferta dla",
  "Na WWW",
  "Pracuję nad",
  "Aktywność",
  "Segment",
  "Inne",
  "TravelPay",
];

interface AddTagProps {
  readonly onAdd: (category: CustomerTagCategory, name: string) => Promise<void>;
}

const AddTagRow: FC<AddTagProps> = ({ onAdd }) => {
  const [category, setCategory] = useState<CustomerTagCategory>("Obszar");
  const [name, setName] = useState("");
  return (
    <TableBody>
      <TableRow>
        <TableCell>
          <Select
            fullWidth
            label="Kategoria"
            values={categories.map((value) => ({ value }))}
            value={category}
            onSelect={setCategory}
          />
        </TableCell>
        <TableCell>
          <TextField fullWidth label="Nazwa" value={name} onChange={(e) => setName(e.target.value)} />
        </TableCell>
        <TableCell>
          <AsyncLoadingButton
            disabled={!name}
            onClick={async () => {
              try {
                await onAdd(category, name);
              } catch {
                // Already reported in a snackbar; keep the input so it can be corrected and retried.
                return;
              }
              setCategory("Obszar");
              setName("");
            }}
          >
            Dodaj
          </AsyncLoadingButton>
        </TableCell>
      </TableRow>
    </TableBody>
  );
};

interface TagRowProps {
  readonly tag: CustomerTag;
  readonly onUpdate: (category: CustomerTagCategory, name: string) => Promise<void>;
  readonly onDelete: () => Promise<void>;
}

const TagRow: FC<TagRowProps> = ({ tag, onUpdate, onDelete }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  return (
    <TableRow data-testid="tag-row">
      <TableCell>{tag.category}</TableCell>
      <TableCell>{tag.name}</TableCell>
      <TableCell>
        <Stack direction="row" spacing={2}>
          <IconButton size="small" aria-label="Edytuj nazwę taga" onClick={() => setDialogOpen(true)}>
            <EditIcon />
          </IconButton>
          <IconButton onClick={onDelete} aria-label="Usuń tag ze słownika">
            <DeleteIcon />
          </IconButton>
        </Stack>
        <EditCustomerTagDialog
          open={dialogOpen}
          category={tag.category}
          tagName={tag.name}
          onChange={onUpdate}
          onClose={() => setDialogOpen(false)}
        />
      </TableCell>
    </TableRow>
  );
};

const CustomerTagDictionary = () => {
  const { tags, isLoading, addTag, updateTag, deleteTag } = useCustomerTags();
  return (
    <DictionaryAccordion title="Tagi klienta" isLoading={isLoading}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Kategoria</TableCell>
            <TableCell>Nazwa</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <AddTagRow
          onAdd={async (c, n) => {
            await addTag(c, n);
          }}
        />
        <TableBody>
          {tags.map((tag) => (
            <TagRow
              key={tag.id}
              tag={tag}
              onUpdate={async (category, name) => {
                await updateTag(tag.id, category, name);
              }}
              onDelete={async () => {
                await deleteTag(tag.id);
              }}
            />
          ))}
        </TableBody>
      </Table>
    </DictionaryAccordion>
  );
};

export default CustomerTagDictionary;
